import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import swal from 'sweetalert2';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { PublicidadDashboard } from 'src/app/shared/model/publicidad-dashboard/publicidadDashboard.model';
import { Msg } from 'src/app/utilerias/mensajes/Msg';
import { ModelPublicidadComponent } from '../model-publicidad/model-publicidad.component';
import { DashboardService } from '../service/dashboard.service';

@Component({
  standalone: true,
  selector: 'app-gestion-publicidad',
  templateUrl: './gestion-publicidad.component.html',
  imports: [FormsModule, MatButtonModule, NgIf, NgFor],
})
export class GestionPublicidadComponent implements OnInit {
  listaPublicidades: PublicidadDashboard[] = [];
  consultaObject = new ConsultaList<PublicidadDashboard>();
  totalRegistros = 0;
  pageSize = 10;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;

  constructor(
    private dialog: MatDialog,
    private msg: Msg,
    private service: DashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = Math.max(1, page);
    this.loading = this.listaPublicidades.length === 0;
    this.cdr.detectChanges();

    this.consultaObject.regInicial = (this.currentPage - 1) * this.pageSize;
    this.consultaObject.regFinal = this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;
    this.consultaObject.param1 = 'SI';

    this.service.consultarpublicidad(this.consultaObject).subscribe({
      next: (res) => {
        const rows = Array.isArray(res?.list) ? res.list : [];
        this.totalRegistros = this.resolveTotalRegistros(res);
        this.listaPublicidades = [...rows];

        if (rows.length > 0) {
          this.cargarImagenes(this.listaPublicidades);
        } else if (this.currentPage === 1) {
          this.msg.warningCall('Sin registros para mostrar', null, null);
        }

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaPagina(maxPages);
          return;
        }

        if (maxPages === 0) {
          this.currentPage = 1;
        }

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listaPublicidades = [];
        this.totalRegistros = 0;
        this.currentPage = 1;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  altaModificarPublicidad(objectP: PublicidadDashboard | null): void {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    const dialogRef = this.dialog.open(ModelPublicidadComponent, {
      disableClose: true,
      autoFocus: true,
      width: '60%',
      data: objectP,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  eliminarTarjeta(publicidad: PublicidadDashboard): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.service.eliminarTarjetaDePublicidad(publicidad).subscribe({
      next: (res) => {
        if (res != null && Number(res.pCodRetorno) === 0) {
          swal.fire({
            title: 'Se ha eliminado la tarjeta de publicidad seleccionada.',
            timer: 5000,
            icon: 'success',
            showConfirmButton: true,
          }).then(() => {
            this.consultaPagina(this.currentPage);
          });
        } else {
          // Solo la plataforma borra publicidad: el servidor dice por que no.
          swal.fire({
            title: 'Falló al eliminar la tarjeta de publicidad seleccionada.',
            text: res?.pMensaje || '',
            timer: 5000,
            icon: 'error',
            showConfirmButton: true,
          }).then(() => {
            this.loading = false;
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          });
        }
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        swal.fire({
          title: 'Falló al eliminar la tarjeta de publicidad seleccionada.',
          timer: 5000,
          icon: 'error',
          showConfirmButton: true,
        });
      },
    });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }

    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private cargarImagenes(publicidades: PublicidadDashboard[]): void {
    publicidades.forEach((element) => {
      this.service.getImagenPublicidad(element).subscribe((res) => {
        const arreglo = new Uint8Array(res);
        let imgtxt = '';
        arreglo.forEach((byte: number) => {
          imgtxt += String.fromCodePoint(byte);
        });
        const base64Img = btoa(imgtxt);
        element.imagenConvert = 'data:image/jpg;base64,' + base64Img;
        this.cdr.detectChanges();
      });
    });
  }

  private resolveTotalRegistros(res: ConsultaList<PublicidadDashboard>): number {
    const response = res as ConsultaList<PublicidadDashboard> & Record<string, unknown>;
    const candidates = [response?.param4, response?.pTotalItems, response?.totalItems, response?.totalRegistros];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return Array.isArray(res?.list) ? res.list.length : 0;
  }
}
